import type { DataTableRowEditCompleteEvent } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import type {
  APIColumnDataSource,
  APIColumnDataSourceConfig,
  APITable,
  ColumnDataSourceKeyValueDataType,
  RelationColumnDataSourceConfig,
} from "../../../../../../../api/infrastructure/table.formatter";
import type { TableColumnTempInput } from "../../../../../../../api/infrastructure/table.transformer";
import { TABLE_COLUMN_TYPES } from "../../../../../../../constants";
import { useSidebar } from "../../../../../../providers/SidebarProvider";
import { transformToValidTableColumnName } from "../../../../../../shared/add-table-column";
import { syncOpenModalDialog } from "../../../../../../shared/global-tools/dialog-system";
import { showToast } from "../../../../../../shared/global-tools/toast-system";
import makeConfirmDialog from "../../../../../utils/ConfirmDialog/ConfirmDialog";
import Icon from "../../../../../utils/Icons/Icon";
import OurBadge from "../../../../../utils/OurBadge/OurBadge";
import OurDataTable, { type ColumnType } from "../../../../../utils/OurDataTable/OurDataTable";
import OurDropdown from "../../../../../utils/OurDropdown/OurDropdown";
import OurSwitchInput from "../../../../../utils/OurSwitchInput/OurSwitchInput";
import SwitchBadge from "../../../../../utils/SwitchBadge/SwitchBadge";
import RelationTableColumnForm from "../AddTableField/RelationTableColumnForm";
import TableColumnAPIForm from "../AddTableField/TableColumnAPIForm/TableColumnAPIForm";
import TableColumnEnumForm from "../AddTableField/TableColumnEnumForm/TableColumnEnumForm";
import "./index.scss";

// TODO: implementing above form edit instead of datatable editor

export interface TableColumnDataTableProps {
  currentTable: APITable;
  tables: APITable[];
  tableColumnInputs: TableColumnTempInput[];
  onChange(updatedFields: TableColumnTempInput[]): void;
}

export default function TableColumnDataTable({
  currentTable,
  tables,
  onChange,
  tableColumnInputs,
}: TableColumnDataTableProps) {
  const [keyValues, setKeyValues] = useState<ColumnDataSourceKeyValueDataType[]>();
  const [apiConfig, setApiConfig] = useState<APIColumnDataSourceConfig>();
  const [relationConfig, setRelationConfig] = useState<RelationColumnDataSourceConfig>();
  const { openSidebar, closeSidebar } = useSidebar();

  const deleteColumn = (column: TableColumnTempInput) =>
    syncOpenModalDialog(makeConfirmDialog, {
      onConfirm: () => handleColumnDeletion(column.name),
      warningMessage: (
        <>
          Voulez-vous supprimer le champ <b>{column.name}</b> ?
        </>
      ),
    });

  const handleColumnDeletion = (name: string) => {
    const index = tableColumnInputs.findIndex((f) => f.name === name);
    if (index !== -1) {
      tableColumnInputs.splice(index, 1);
    }
    onChange([...tableColumnInputs]);
  };

  const actionBtnsBodyTemplate = (column: TableColumnTempInput) => {
    return (
      <button type="button" className="IconBtn" onClick={() => deleteColumn(column)}>
        <Icon name="trash" color="#EF0404" width={17} height={17} />
      </button>
    );
  };

  const columns: ColumnType<TableColumnTempInput>[] = [
    {
      field: "deleteAction",
      header: "Suppression",
      body: (datum) => actionBtnsBodyTemplate(datum),
    },
    {
      field: "editActions",
      header: "Modification",
      headerStyle: { width: "10%", minWidth: "4rem" },
      bodyStyle: { textAlign: "center" },
      rowEditor: true,
    },
    {
      field: "label",
      header: "Libellé du champ",
      style: { minWidth: "50px" },
      editor: (options) => (
        <InputText
          className="Field-input small TableColumnDataTable-input"
          value={options.value}
          onChange={(e) => {
            options.editorCallback?.(e.target.value);
          }}
        />
      ),
    },
    {
      field: "type",
      header: "Type de champ",
      style: { minWidth: "50px" },
      body(datum) {
        if (!datum.type) return "-";
        return (
          <OurBadge status={getColumnTypeStatus(datum.type)} label={datum.type.toUpperCase()} />
        );
      },
      editor: (options) => {
        return (
          <OurDropdown<string>
            initialOptions={TABLE_COLUMN_TYPES}
            className="sm TableColumnDataTable-dropdown"
            selectedOption={options.value}
            alwaysTriggerOnChange={true}
            rowData={options?.rowData}
            onOptionItemClick={(rowData) => {
              if (rowData.type === "enum-api") {
                setApiConfig(apiConfig ?? rowData?.dataSource?.config);
              } else {
                setRelationConfig(relationConfig ?? rowData?.dataSource?.config);
              }
              setKeyValues(keyValues ?? rowData?.dataSource?.data);
            }}
            onChange={(val) => {
              if (val === "enum-key-value") {
                openSidebar({
                  modal: true,
                  body: (
                    <TableColumnEnumForm
                      initialKeyValues={keyValues}
                      close={closeSidebar}
                      onChange={setKeyValues}
                    />
                  ),
                  position: "right",
                });
              }
              if (val === "enum-api") {
                openSidebar({
                  modal: true,
                  body: (
                    <TableColumnAPIForm
                      close={closeSidebar}
                      apiConfig={apiConfig}
                      onChange={setApiConfig}
                    />
                  ),
                  position: "right",
                });
              }
              if (val === "enum-relation") {
                openSidebar({
                  modal: true,
                  body: (
                    <RelationTableColumnForm
                      currentTable={currentTable}
                      tables={tables}
                      close={closeSidebar}
                      relationConfig={relationConfig}
                      onChange={setRelationConfig}
                    />
                  ),
                  position: "right",
                });
              }
              options.editorCallback?.(val);
            }}
          />
        );
      },
    },
    {
      field: "length",
      header: "Longueur et précision",
      style: { minWidth: "50px" },
      editor: (options) => (
        <InputNumber
          className="Field-input small TableColumnDataTable-input"
          value={options.value}
          onChange={(e) => options.editorCallback?.(e.value ?? undefined)}
          min={1}
          max={255}
        />
      ),
    },
    {
      field: "unique",
      header: "Unique",
      style: { minWidth: "50px" },
      body(datum) {
        return <SwitchBadge status={datum.unique} />;
      },
      editor: (options) => (
        <OurSwitchInput value={options.value} onChange={(val) => options.editorCallback?.(val)} />
      ),
    },
    {
      field: "required",
      header: "Est obligatoire ?",
      style: { minWidth: "50px" },
      body(datum) {
        return <SwitchBadge status={datum.required} />;
      },
      editor: (options) => (
        <OurSwitchInput value={options.value} onChange={(val) => options.editorCallback?.(val)} />
      ),
    },
  ];

  const onRowEditComplete = (event: DataTableRowEditCompleteEvent) => {
    const { newData, index } = event;
    const label = newData.label;
    if (label === "") {
      showToast("Le nom du champ est requis", {
        severity: "warn",
      });
      return;
    }

    const validColumnName = transformToValidTableColumnName(label);
    const isNameTaken = tableColumnInputs.some(
      (col) => col.name === validColumnName && col.name !== tableColumnInputs[index].name,
    );
    if (isNameTaken) {
      showToast("Le libellé du champ est déjà utilisé", {
        severity: "warn",
      });
      return;
    }

    const type = newData.type;
    if (type !== "string" && type !== "number") {
      newData.length = undefined;
    }

    let dataSource: APIColumnDataSource | undefined;
    if (type === "enum-key-value") {
      if (keyValues && keyValues.length > 0) {
        dataSource = {
          type: "key_value",
          data: keyValues,
        };
      }
    } else if (type === "enum-api") {
      if (apiConfig) {
        dataSource = {
          type: "api",
          config: apiConfig,
        };
      }
    }

    const newTableColumns = [...tableColumnInputs];
    newTableColumns[index] = { ...(newData as TableColumnTempInput), dataSource };
    onChange(newTableColumns);
  };

  return (
    <OurDataTable<TableColumnTempInput>
      data={tableColumnInputs}
      columns={columns}
      options={{ stripedRows: true, rows: 10, editMode: "row", onRowEditComplete }}
      className="TableColumnDataTable"
    />
  );
}

function getColumnTypeStatus(type: string): "success" | "error" | "info" {
  if (type === "string") return "success";
  if (type === "number" || type === "boolean") return "info";
  if (type === "date") return "info";
  return "info";
}
